import React, {useCallback, useEffect, useMemo} from "react";
import "./tabs.sass";
import classNames from "classnames";
import {observer} from "mobx-react-lite";
import {Dictionary} from "lodash";
import {useHistory} from "react-router";
import {QueryParams} from "../../../shared/enums/queryParams";

export interface Tab {
  name: string;
  content: React.ReactNode;
  children?: React.ReactNode;
  onClick?: Function;
  testId?: string;
}

export interface TabsProps {
  tabs: Tab[];
  headerClassNames?: string;
}

const Tabs: React.FC<TabsProps> = ({tabs, headerClassNames}) => {
  const key = QueryParams.Tab;
  const tabsDict: Dictionary<Tab> = useMemo(
    () =>
      tabs.reduce((acc, cur) => {
        acc[cur.name] = cur;
        return acc;
      }, {} as Dictionary<Tab>),
    [tabs]
  );

  const history = useHistory();
  const location = history.location;
  // Even though after clicking on the tab it is supposed to navigate,
  //   but sometimes it is blocked by the consumer of this component.
  //   Hence, we need to read the the value of params everytime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = new URLSearchParams(location.search);

  let tabName: string = "";
  try {
    tabName = decodeURI(params.get(key) || "");
  } catch (e) {}

  const updateTabNameInQueryParam = useCallback(
    (tabName: string) => {
      params.delete(key);
      params.append(key, encodeURI(tabName));
      history.push({
        ...history.location,
        search: params.toString()
      });
    },
    [history, key, params]
  );

  useEffect(() => {
    // Selected tab defaults to the first one.
    if (!tabsDict[tabName]) {
      updateTabNameInQueryParam(tabs[0].name);
    }
  }, [history, params, tabName, tabs, tabsDict, updateTabNameInQueryParam]);

  const options = tabs.map(({name, children, testId}) => (
    <div
      key={name}
      data-testid={testId}
      className={classNames(headerClassNames, {
        selected: tabName === name
      })}
      onClick={() => {
        const onClick = tabsDict[name].onClick;
        updateTabNameInQueryParam(name);
        onClick?.();
      }}
    >
      {children ?? name}
    </div>
  ));
  const body = tabsDict[tabName]?.content;
  return (
    <div className="tabs">
      <div className="header" data-testid="eventTabsHeader">
        {options}
      </div>
      {body}
    </div>
  );
};

export default observer(Tabs);
